<div class="flex flex-col h-full overflow-hidden">
  <!-- Header -->
  <div class="flex flex-col px-4 sm:px-6 py-2.5 bg-gradient-to-r from-gray-50 to-white border-b border-gray-200 flex-shrink-0">
    <!-- Title and Buttons Row -->
    <div class="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
      <div class="flex items-center gap-2 min-w-0 text-sm">
        <h2 class="font-semibold text-gray-900 shrink-0">Committee Audit Log</h2>
        @if (projectName() || committee()?.Name) {
          <span class="text-gray-300">&mdash;</span>
          @if (projectName()) {
            <span class="text-gray-500 truncate">{{ projectName() }}</span>
          }
          @if (projectName() && committee()?.Name) {
            <span class="text-gray-300">/</span>
          }
          @if (committee()?.Name) {
            <span class="text-gray-600 truncate font-medium">{{ committee()!.Name }}</span>
          }
        }
      </div>
      <div class="flex gap-2 items-center flex-shrink-0">
        @if (!hasError() && !loading()) {
          <div class="relative z-20">
            <button pButton class="relative z-20 p-button-outlined header-action-btn" [class.p-button-info]="isFiltersVisible()" (click)="toggleFilters()">
              <i class="fa-regular fa-filter text-sm mr-2"></i>
              <span class="hidden sm:inline">{{ isFiltersVisible() ? 'Hide Filters' : 'Filters' }}</span>
              <span class="sm:hidden">{{ isFiltersVisible() ? 'Hide' : 'Filter' }}</span>
              @if (hasActiveFilters() && !isFiltersVisible()) {
                <span class="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-medium rounded-full ml-1">
                  {{ activeFilterCount() }}
                </span>
              }
            </button>

            <!-- Filters Panel (dropdown overlay) -->
            @if (isFiltersVisible()) {
              <div class="fixed inset-0 z-10" (click)="isFiltersVisible.set(false)"></div>
              <lfx-committee-audit-filters-panel
                class="absolute right-0 top-full mt-1 z-20"
                [activeFilterCount]="activeFilterCount()"
                [(period)]="period"
                [periodOptions]="rangeOptions"
                [(dateFrom)]="dateFrom"
                [(dateTo)]="dateTo"
                [(selectedTypes)]="selectedTypes"
                [(selectedActions)]="selectedActions"
                [(selectedCommittees)]="selectedCommittees"
                [(selectedMembers)]="selectedMembers"
                [(selectedUsers)]="selectedUsers"
                [(selectedFields)]="selectedFields"
                [typeOptions]="filterTypes()"
                [actionOptions]="filterActions()"
                [committeeOptions]="filterCommittees()"
                [memberOptions]="filterMembers()"
                [userOptions]="filterUsers()"
                [fieldOptions]="filterFields()"
                [showCommitteeFilter]="!committeeId()"
                (periodChange)="resetPage()"
                (dateFromChange)="resetPage()"
                (dateToChange)="resetPage()"
                (selectedTypesChange)="resetPage()"
                (selectedActionsChange)="resetPage()"
                (selectedCommitteesChange)="resetPage()"
                (selectedMembersChange)="resetPage()"
                (selectedUsersChange)="resetPage()"
                (selectedFieldsChange)="resetPage()"
                (filtersCleared)="resetPage()" />
            }
          </div>
        }
        @if (!hasError() && auditLogs().length > 0) {
          <button pButton class="header-action-btn" title="Export Audit Log" (click)="onExport()">
            <i class="fa-regular fa-download text-sm mr-2"></i>
            <span class="hidden sm:inline">Export</span>
          </button>
        }
      </div>
    </div>
  </div>

  <!-- Content -->
  @if (hasError()) {
    <div class="flex flex-col items-center justify-center py-12 sm:py-20 px-4 sm:px-8 text-center bg-gray-50 flex-1">
      <div class="bg-red-50 rounded-full p-4 sm:p-6 mb-4">
        <i class="fa-regular fa-exclamation-circle text-4xl sm:text-5xl text-red-500"></i>
      </div>
      <p class="text-base sm:text-lg font-semibold text-gray-900 mb-2">Failed to load audit logs</p>
      <p class="text-sm text-gray-500 max-w-md">Please try again later or contact support if the issue persists</p>
    </div>
  } @else {
    @if (loading()) {
      <!-- Loading skeleton -->
      <div class="flex-1 overflow-hidden flex flex-col min-h-0">
        <div class="overflow-auto flex-1 min-h-0">
          <table class="audit-table">
            <ng-container [ngTemplateOutlet]="tableHeader"></ng-container>
            <tbody>
              @for (i of [1, 2, 3, 4, 5]; track $index) {
                <tr>
                  <td class="date-cell">
                    <p-skeleton width="150px" height="14px"></p-skeleton>
                  </td>
                  <td class="user-cell">
                    <div class="flex items-center gap-2">
                      <p-skeleton shape="circle" size="2rem"></p-skeleton>
                      <div class="flex flex-col gap-1">
                        <p-skeleton width="100px" height="14px"></p-skeleton>
                        <p-skeleton width="130px" height="11px"></p-skeleton>
                      </div>
                    </div>
                  </td>
                  <td class="action-cell">
                    <p-skeleton width="80px" height="14px"></p-skeleton>
                  </td>
                  @if (!committeeId()) {
                    <td class="committee-cell">
                      <p-skeleton width="100px" height="14px"></p-skeleton>
                    </td>
                  }
                  <td class="type-cell">
                    <p-skeleton width="90px" height="14px"></p-skeleton>
                  </td>
                  <td class="member-cell">
                    <p-skeleton width="100px" height="14px"></p-skeleton>
                  </td>
                  <td class="field-cell">
                    <p-skeleton width="80px" height="14px"></p-skeleton>
                  </td>
                  <td class="value-cell">
                    <p-skeleton width="120px" height="13px"></p-skeleton>
                  </td>
                  <td class="value-cell">
                    <p-skeleton width="110px" height="13px"></p-skeleton>
                  </td>
                </tr>
              }
            </tbody>
          </table>
        </div>
      </div>
    } @else {
      <div class="flex flex-col flex-1 bg-white overflow-hidden">
        @if (filteredAuditLogs().length > 0) {
          <!-- Table View -->
          <div class="flex-1 overflow-hidden flex flex-col min-h-0">
            <div #scrollContainer class="overflow-auto flex-1 min-h-0">
              <table class="audit-table">
                <ng-container [ngTemplateOutlet]="tableHeader"></ng-container>
                <tbody>
                  @for (group of groupedPaginatedLogs(); track group.groupId) {
                    @for (log of group.logs; track $index) {
                      <tr
                        [class.grouped-row]="group.logs.length > 1"
                        [class.first-in-group]="$first && group.logs.length > 1"
                        [class.middle-in-group]="!$first && !$last && group.logs.length > 1"
                        [class.last-in-group]="$last && group.logs.length > 1">
                        <!-- Date & Time -->
                        <td class="date-cell">
                          <span class="text-sm text-gray-900 whitespace-nowrap"
                            >{{ log.createdAt | date: 'MMM d, y' }} &bull; {{ log.createdAt | date: 'h:mm a' }}</span
                          >
                        </td>

                        <!-- User -->
                        <td class="user-cell">
                          <div class="flex items-center gap-2">
                            @if (log.auditUser.Avatar && log.auditUser.Avatar !== '') {
                              <p-avatar
                                [image]="log.auditUser.Avatar"
                                (onImageError)="onImageError($event)"
                                shape="circle"
                                size="normal"
                                styleClass="avatar-image-fit"
                                [style]="{ 'min-width': '32px', 'min-height': '32px', width: '32px', height: '32px' }"></p-avatar>
                            } @else {
                              <p-avatar
                                [label]="log.auditUser.Name || 'U' | initial"
                                shape="circle"
                                size="normal"
                                [style]="{ 'min-width': '32px', 'min-height': '32px', width: '32px', height: '32px' }"></p-avatar>
                            }
                            <div class="flex flex-col min-w-0">
                              <span class="text-sm font-medium text-gray-900 leading-tight truncate">
                                {{ log.auditUser.Name || log.auditUser.UserName || 'Unknown User' }}
                              </span>
                              @if (log.auditUser.Email) {
                                <span class="text-xs text-gray-500 leading-tight truncate">
                                  {{ log.auditUser.Email }}
                                </span>
                              }
                            </div>
                          </div>
                        </td>

                        <!-- Action -->
                        <td class="action-cell">
                          <span class="text-sm font-medium" [class]="log.actionCssClass">
                            {{ log.action | titlecase }}
                          </span>
                        </td>

                        <!-- Committee (only for all committees view) -->
                        @if (!committeeId()) {
                          <td class="committee-cell">
                            <span class="text-sm text-gray-700">{{ log.committeeName || '—' }}</span>
                          </td>
                        }

                        <!-- Type -->
                        <td class="type-cell">
                          <span class="text-sm text-gray-700">{{ log.type }}</span>
                        </td>

                        <!-- Member -->
                        <td class="member-cell">
                          <span class="text-sm text-gray-700">{{ log.memberName || '—' }}</span>
                        </td>

                        <!-- Field -->
                        <td class="field-cell">
                          <span class="text-sm font-semibold text-gray-900">{{ log.field }}</span>
                        </td>

                        <!-- Previous Value -->
                        <td class="value-cell">
                          <div class="relative group">
                            <div class="value-content text-gray-600" [innerHTML]="log.from"></div>
                            @if (log.field === 'Description' && log.originalFrom && log.originalFrom !== '-') {
                              <button
                                pButton
                                class="p-button-text p-button-sm p-button-rounded copy-btn"
                                (click)="onCopyToClipboard(log.originalFrom)"
                                [pTooltip]="log.originalFrom"
                                tooltipPosition="top"
                                title="Copy to clipboard">
                                <i class="fa-regular fa-copy text-xs"></i>
                              </button>
                            }
                          </div>
                        </td>

                        <!-- New Value -->
                        <td class="value-cell">
                          <div class="relative group">
                            <div class="value-content text-gray-900 font-semibold" [innerHTML]="log.to"></div>
                            @if (log.field === 'Description' && log.originalTo && log.originalTo !== '-') {
                              <button
                                pButton
                                class="p-button-text p-button-sm p-button-rounded copy-btn"
                                (click)="onCopyToClipboard(log.originalTo)"
                                [pTooltip]="log.originalTo"
                                tooltipPosition="top"
                                title="Copy to clipboard">
                                <i class="fa-regular fa-copy text-xs"></i>
                              </button>
                            }
                          </div>
                        </td>
                      </tr>
                    }
                  }
                </tbody>
              </table>
            </div>
          </div>
          <div class="flex items-center justify-between px-4 sm:px-6 py-2 bg-white border-t border-gray-200 flex-shrink-0">
            @if (hasActiveFilters()) {
              <span class="text-xs text-gray-500 shrink-0">
                <i class="fa-regular fa-filter mr-1"></i>{{ filteredAuditLogs().length }} of {{ auditLogs().length }}
              </span>
            }
            <p-paginator
              class="flex-1"
              [first]="paginatorFirst()"
              [rows]="pageSize()"
              [totalRecords]="filteredAuditLogs().length"
              [rowsPerPageOptions]="rowsPerPageOptions"
              [showCurrentPageReport]="true"
              currentPageReportTemplate="{first}–{last} of {totalRecords}"
              (onPageChange)="onPageChange($event)">
            </p-paginator>
          </div>
        } @else {
          <!-- Empty State -->
          <div class="flex flex-col items-center justify-center py-12 sm:py-20 px-4 sm:px-8 text-center bg-gray-50 flex-1">
            <div class="bg-white rounded-full p-4 sm:p-6 mb-4 shadow-sm">
              <i class="fa-regular fa-clipboard-list text-4xl sm:text-5xl text-gray-400"></i>
            </div>
            <p class="text-base sm:text-lg font-semibold text-gray-900 mb-2">No audit logs found</p>
            @if (hasActiveFilters()) {
              <p class="text-sm text-gray-500 max-w-md">Try adjusting your filters to see more results</p>
            } @else {
              <p class="text-sm text-gray-500 max-w-md">
                @if (committeeId()) {
                  There are no audit logs available for {{ committee()?.Name ? '"' + committee()!.Name + '"' : 'this committee' }}
                } @else {
                  There are no audit logs available for these committees
                }
              </p>
            }
          </div>
        }
      </div>
    }
  }
</div>

<!-- Shared table header template -->
<ng-template #tableHeader>
  <thead>
    <tr>
      <th scope="col">
        <div class="flex items-center gap-1.5">
          <i class="fa-regular fa-calendar-clock"></i>
          <span>Date & Time</span>
        </div>
      </th>
      <th scope="col">
        <div class="flex items-center gap-1.5">
          <i class="fa-regular fa-user-edit"></i>
          <span>User</span>
        </div>
      </th>
      <th scope="col">
        <div class="flex items-center gap-1.5">
          <i class="fa-regular fa-bolt"></i>
          <span>Action</span>
        </div>
      </th>
      @if (!committeeId()) {
        <th scope="col">
          <div class="flex items-center gap-1.5">
            <i class="fa-regular fa-users"></i>
            <span>Committee</span>
          </div>
        </th>
      }
      <th scope="col">
        <div class="flex items-center gap-1.5">
          <i class="fa-regular fa-tag"></i>
          <span>Type</span>
        </div>
      </th>
      <th scope="col">
        <div class="flex items-center gap-1.5">
          <i class="fa-regular fa-user"></i>
          <span>Member</span>
        </div>
      </th>
      <th scope="col">
        <div class="flex items-center gap-1.5">
          <i class="fa-regular fa-file-lines"></i>
          <span>Field</span>
        </div>
      </th>
      <th scope="col">
        <div class="flex items-center gap-1.5">
          <i class="fa-regular fa-arrow-left"></i>
          <span>Previous Value</span>
        </div>
      </th>
      <th scope="col">
        <div class="flex items-center gap-1.5">
          <i class="fa-regular fa-arrow-right"></i>
          <span>New Value</span>
        </div>
      </th>
    </tr>
  </thead>
</ng-template>
